import React from 'react';

const STEPS = [
  {
    num: '1',
    icon: '✨',
    title: 'Generate',
    desc: 'Go to any story with a brief, click "Create Social Post" to auto-generate slides from the story content. Choose your target platform (Instagram, Facebook, etc.).',
  },
  {
    num: '2',
    icon: '🎨',
    title: 'Design & Edit',
    desc: 'Use the left sidebar to customize each slide — change headlines, body text, background colors, images, and overlays. Switch to the "All Slides & Post" tab to apply global design, gradients, or pick a template.',
  },
  {
    num: '3',
    icon: '✍️',
    title: 'Write Caption & Hashtags',
    desc: 'In the "All Slides & Post" tab, craft your caption and hashtags. These will be included in the export alongside your slides.',
  },
  {
    num: '4',
    icon: '🖼️',
    title: 'Render Slides',
    desc: 'Click "Render All Slides" in the Actions section to generate final high-quality images. Each slide is rendered as a PNG ready for social media.',
  },
  {
    num: '5',
    icon: '📦',
    title: 'Export ZIP',
    desc: 'Click "Export ZIP" to download all rendered slides plus a caption text file — everything you need to publish manually.',
  },
  {
    num: '6',
    icon: '📅',
    title: 'Schedule (Optional)',
    desc: 'Set a publish date & time via the Schedule button. Scheduled posts will be automatically published when connected to a social channel.',
  },
];

const TIPS = [
  { icon: '↩️', text: 'Undo / Redo — use the buttons in the sidebar to revert any change.' },
  { icon: '🖱️', text: 'Drag to reorder — drag slide thumbnails in the sidebar to rearrange order.' },
  { icon: '🎨', text: 'Apply to All — use the "All ↗" buttons next to colors to apply one color across every slide.' },
  { icon: '🌈', text: 'Gradient — create a smooth color gradient across all slides from first to last color.' },
  { icon: '➕', text: 'Add slides — click "+" at the bottom of the slide thumbnails to insert blank or duplicated slides.' },
  { icon: '🔄', text: 'Status flow — Draft → Ready → Approved. Move your post through review stages.' },
];

export default function HelpModal({ open, onClose, context }) {
  if (!open) return null;

  const showEditorTips = context === 'editor';

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="tw-bg-zinc-900 tw-border tw-border-zinc-700 tw-rounded-2xl tw-p-6 tw-w-full tw-max-w-lg tw-shadow-2xl tw-max-h-[85vh] tw-overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
          <h3 className="tw-text-sm tw-font-bold tw-text-white">How Social Posts Work</h3>
          <button
            onClick={onClose}
            className="tw-text-zinc-500 hover:tw-text-white tw-transition-colors tw-text-lg tw-leading-none"
          >
            ✕
          </button>
        </div>

        {/* Steps */}
        <div className="tw-space-y-3 tw-mb-5">
          {STEPS.map((s) => (
            <div key={s.num} className="tw-flex tw-gap-3 tw-items-start">
              <span className="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-bg-violet-500/20 tw-text-violet-400 tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center">
                {s.num}
              </span>
              <div>
                <div className="tw-text-xs tw-font-semibold tw-text-white tw-mb-0.5">
                  {s.icon} {s.title}
                </div>
                <div className="tw-text-[11px] tw-text-zinc-400 tw-leading-relaxed">
                  {s.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Editor Tips (only in editor context) */}
        {showEditorTips && (
          <>
            <div className="tw-border-t tw-border-zinc-800 tw-pt-4 tw-mb-3">
              <h4 className="tw-text-xs tw-font-bold tw-text-zinc-300 tw-mb-3">Editor Tips</h4>
            </div>
            <div className="tw-space-y-2 tw-mb-5">
              {TIPS.map((t, i) => (
                <div key={i} className="tw-flex tw-gap-2 tw-items-start tw-text-[11px] tw-text-zinc-400">
                  <span className="tw-flex-shrink-0">{t.icon}</span>
                  <span>{t.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          onClick={onClose}
          className="tw-w-full tw-btn-secondary tw-text-xs"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
